import React, { useState } from 'react'
import { Button } from '@wove/web-ui'
import { Search, RefreshCw, AlertTriangle, Building, Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  useCargowiseOrganizations,
  useSearchCargowiseOrganizations,
  useCreateCargowiseOrganization,
  useUpdateCargowiseOrganization,
  useDeleteCargowiseOrganization,
  CargowiseOrganization,
} from '@wove/api'
import CargowiseOrganizationsTable from './CargowiseOrganizationsTable'
import CargowiseOrganizationFormDialog from './CargowiseOrganizationFormDialog'
import DeleteConfirmationDialog from './DeleteConfirmationDialog'

export default function CargowiseOrganizations() {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<CargowiseOrganization[] | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<CargowiseOrganization | null>(
    null,
  )
  const [organizationToDelete, setOrganizationToDelete] = useState<CargowiseOrganization | null>(
    null,
  )

  const { data: organizations = [], isLoading, error, refetch } = useCargowiseOrganizations()

  const searchMutation = useSearchCargowiseOrganizations()
  const createMutation = useCreateCargowiseOrganization()
  const updateMutation = useUpdateCargowiseOrganization()
  const deleteMutation = useDeleteCargowiseOrganization()

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null)
      return
    }

    try {
      const results = await searchMutation.mutateAsync(searchQuery)
      setSearchResults(results)
    } catch (error) {
      toast.error('Failed to search organizations')
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setSearchResults(null)
  }

  const handleCreate = () => {
    setSelectedOrganization(null)
    setIsFormOpen(true)
  }

  const handleEdit = (organization: CargowiseOrganization) => {
    setSelectedOrganization(organization)
    setIsFormOpen(true)
  }

  const handleDelete = (organization: CargowiseOrganization) => {
    setOrganizationToDelete(organization)
  }

  const handleFormSubmit = async (data: any) => {
    try {
      if (selectedOrganization) {
        await updateMutation.mutateAsync({
          organizationId: selectedOrganization.id,
          data,
        })
        toast.success('Organization updated successfully.')
      } else {
        await createMutation.mutateAsync(data)
        toast.success('Organization created successfully.')
      }
      setIsFormOpen(false)
      setSelectedOrganization(null)
    } catch (error) {
      toast.error(
        selectedOrganization
          ? 'Failed to update organization. Please try again.'
          : 'Failed to create organization. Please try again.',
      )
    }
  }

  const handleDeleteConfirm = async () => {
    if (!organizationToDelete) return

    try {
      await deleteMutation.mutateAsync(organizationToDelete.id)
      toast.success('Organization deleted successfully.')
      setOrganizationToDelete(null)
    } catch (error) {
      toast.error('Failed to delete organization. Please try again.')
    }
  }

  const displayData = searchResults || organizations
  const isSearchMode = !!searchResults

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">CargoWise Organizations</h2>
          <p className="text-muted-foreground">Browse and search CargoWise organization data</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading organizations</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="text-red-700 border-red-300 hover:bg-red-100"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">CargoWise Organizations</h2>
        <p className="text-muted-foreground">Browse and search CargoWise organization data</p>
      </div>

      {/* Search Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search organizations by name, code, city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <Button onClick={handleSearch} disabled={searchMutation.isPending || !searchQuery.trim()}>
            {searchMutation.isPending ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Search
          </Button>
          {isSearchMode && (
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </div>

        {isSearchMode && (
          <div className="mt-4 text-sm text-gray-600">
            Showing search results for: <span className="font-medium">"{searchQuery}"</span>
          </div>
        )}
      </div>

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium">
            {isSearchMode ? 'Search Results' : 'All Organizations'}
          </h3>
          <span className="text-sm text-gray-500">
            {displayData.length} organization{displayData.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Organization
          </Button>
        </div>
      </div>

      {/* Organizations Table */}
      <CargowiseOrganizationsTable
        organizations={displayData}
        isLoading={isLoading}
        isSearchMode={isSearchMode}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Form Dialog */}
      <CargowiseOrganizationFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        organization={selectedOrganization}
        onSubmit={handleFormSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={!!organizationToDelete}
        onOpenChange={(open) => !open && setOrganizationToDelete(null)}
        title="Delete Organization"
        description={`Are you sure you want to delete "${organizationToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  )
}
